import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Compass, Lock, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { SCALE_HUB_THEME as C } from "../../../lib/scales/scaleHubTheme";
import {
  SCALE_HUB_SECTION_LABELS,
  areCagedCardsUnlocked,
  countScaleHubCards,
  getScaleHubCardsBySection,
  isScaleCardUnlocked,
} from "../../../lib/scales/scaleHubPlan";
import type { ScaleHubCard, ScaleHubProgress, ScaleHubSection } from "../../../lib/scales/scaleHubTypes";

export interface ScaleHubLibraryProps {
  progress: ScaleHubProgress;
  celebrateCardId: string | null;
  onSelectCard: (cardId: string) => void;
}

const SECTIONS: ScaleHubSection[] = ["missions", "caged"];

const SECTION_HERO: Record<ScaleHubSection, { title: string; body: string }> = {
  missions: {
    title: "Recorridos por nivel",
    body: "Tres misiones progresivas: primeros trastes, rango medio e ida y vuelta. Completa las tres para desbloquear CAGED.",
  },
  caged: {
    title: "Dominio CAGED",
    body: "Las cinco formas del sistema CAGED en Do mayor. Desbloqueadas al completar las misiones de Nivel 1–3.",
  },
};

export function ScaleHubLibrary({ progress, celebrateCardId, onSelectCard }: ScaleHubLibraryProps) {
  const [activeSection, setActiveSection] = useState<ScaleHubSection>("missions");
  const cards = getScaleHubCardsBySection(activeSection);
  const completedCount = progress.completedCardIds.length;
  const totalCards = countScaleHubCards();
  const hero = SECTION_HERO[activeSection];
  const cagedUnlocked = areCagedCardsUnlocked(progress);

  return (
    <div className="h-full min-h-0 flex flex-col font-sans overflow-hidden">
      <div className="shrink-0 px-4 sm:px-6 py-5 border-b" style={{ borderColor: C.border }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${C.accentSoft}, transparent)`,
                border: `1px solid ${C.border}`,
              }}
            >
              <Sparkles className="w-5 h-5" style={{ color: C.accent }} />
            </div>
            <div>
              <h1 className="font-serif text-xl font-semibold tracking-tight" style={{ color: C.textMain }}>
                Escalas
              </h1>
              <p className="text-xs" style={{ color: C.textDim }}>
                {completedCount}/{totalCards} misiones · mapa de aprendizaje
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/scales/learn"
              className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl transition-colors hover:opacity-90"
              style={{
                color: "#100f0e",
                background: `linear-gradient(135deg, ${C.accent}, #e85a5a)`,
              }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Misiones POS I (mic)
            </Link>
            <Link
              to="/scales/explorer"
              className="inline-flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-xl transition-colors hover:bg-white/5"
              style={{ color: C.textMuted, border: `1px solid ${C.border}` }}
            >
              <Compass className="w-3.5 h-3.5" style={{ color: C.accent }} />
              Explorador libre
            </Link>
          </div>
        </div>
      </div>

      <div className="shrink-0 px-4 sm:px-6 pt-4 max-w-5xl mx-auto w-full">
        <div className="flex flex-wrap gap-2">
          {SECTIONS.map((section) => (
            <button
              key={section}
              type="button"
              onClick={() => setActiveSection(section)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5"
              style={{
                backgroundColor: activeSection === section ? C.accentSoft : "rgba(255,255,255,0.04)",
                color: activeSection === section ? C.accent : C.textDim,
                border: `1px solid ${activeSection === section ? `${C.accent}44` : C.border}`,
              }}
            >
              {section === "caged" && !cagedUnlocked && <Lock className="w-3 h-3 opacity-70" />}
              {SCALE_HUB_SECTION_LABELS[section]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <HubHero title={hero.title} body={hero.body} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {cards.map((card) => {
              const unlocked = isScaleCardUnlocked(card, progress);
              return (
                <HubCard
                  key={card.id}
                  card={card}
                  unlocked={unlocked}
                  completed={progress.completedCardIds.includes(card.id)}
                  celebrating={celebrateCardId === card.id}
                  onOpen={() => unlocked && onSelectCard(card.id)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function HubHero({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        backgroundColor: C.bgCard,
        border: `1px solid ${C.border}`,
        backgroundImage: `linear-gradient(135deg, ${C.accentSoft}, transparent 55%)`,
      }}
    >
      <h2 className="text-base font-bold mb-1" style={{ color: C.textMain }}>
        {title}
      </h2>
      <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>
        {body}
      </p>
    </div>
  );
}

function HubCard({
  card,
  unlocked,
  completed,
  celebrating,
  onOpen,
}: {
  card: ScaleHubCard;
  unlocked: boolean;
  completed: boolean;
  celebrating: boolean;
  onOpen: () => void;
}) {
  return (
    <motion.button
      type="button"
      layout
      onClick={onOpen}
      disabled={!unlocked}
      animate={
        celebrating
          ? {
              scale: [1, 1.03, 1],
              boxShadow: [
                `0 0 0 0 ${C.accentGlow}`,
                `0 0 24px ${C.accentGlow}`,
                `0 0 0 0 transparent`,
              ],
            }
          : { scale: 1 }
      }
      transition={{ duration: 0.55, ease: "easeOut" }}
      className={`text-left rounded-xl overflow-hidden transition-colors ${
        unlocked ? "hover:brightness-110 cursor-pointer" : "opacity-55 cursor-not-allowed"
      }`}
      style={{
        backgroundColor: C.bgCard,
        border: `1px solid ${celebrating ? C.accent : completed ? "rgba(34,197,94,0.35)" : C.border}`,
      }}
    >
      <div
        className="h-[2px] w-full"
        style={{
          background: completed
            ? "linear-gradient(90deg, #34d399, transparent)"
            : unlocked
              ? `linear-gradient(90deg, ${C.accent}, transparent)`
              : "linear-gradient(90deg, rgba(255,255,255,0.15), transparent)",
        }}
      />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black"
            style={{
              backgroundColor: unlocked ? C.accentSoft : "rgba(255,255,255,0.06)",
              color: unlocked ? C.accent : C.textDim,
            }}
          >
            {unlocked ? "♪" : <Lock className="w-3.5 h-3.5" />}
          </span>
          {completed && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400/90">
              <Check className="w-3 h-3" />
              Completado
            </span>
          )}
          {!unlocked && (
            <span className="text-[10px] font-medium" style={{ color: C.textDim }}>
              Bloqueado
            </span>
          )}
        </div>
        <h3 className="font-semibold text-sm mb-0.5 line-clamp-2" style={{ color: C.textMain }}>
          {card.title}
        </h3>
        <p className="text-xs line-clamp-2 mb-3" style={{ color: C.textMuted }}>
          {card.subtitle}
        </p>
        <div className="flex flex-wrap gap-1">
          {card.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="text-[9px] px-1.5 py-0.5 rounded font-medium"
              style={{ backgroundColor: C.bgElevated, color: C.textDim }}
            >
              {tag}
            </span>
          ))}
        </div>
        <p className="text-[10px] mt-3" style={{ color: C.textDim }}>
          ~{card.timeEstimateMin} min
        </p>
      </div>
    </motion.button>
  );
}
